import type { CompetitionShareOfVoiceSpan, ReviewsGrowthWindow } from '@/gql/reviews/mappers/insights'

import { InsightsTimeframe } from './insightsTimeframe'

export interface InsightsComparisonWindow extends ReviewsGrowthWindow {
  span: CompetitionShareOfVoiceSpan
  months: number
}

const MONTHS_BY_TIMEFRAME = {
  [InsightsTimeframe.LAST_3_MONTHS]: 3,
  [InsightsTimeframe.LAST_6_MONTHS]: 6,
  [InsightsTimeframe.LAST_12_MONTHS]: 12,
}

const MIN_HISTORY_WEEKS = 8

const isoDate = (year: number, month: number, day: number): string => new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10)

export const growthWindow = (timeframe: InsightsTimeframe, today: Date): ReviewsGrowthWindow => {
  const year = today.getFullYear()
  const month = today.getMonth()
  const day = today.getDate()

  if (timeframe === InsightsTimeframe.LATEST) {
    return {
      start: isoDate(year, month, 1),
      end: isoDate(year, month, day),
      previousStart: null,
      previousEnd: null,
    }
  }

  if (timeframe === InsightsTimeframe.YTD) {
    return {
      start: isoDate(year, 0, 1),
      end: isoDate(year, month, day),
      previousStart: isoDate(year - 1, 0, 1),
      previousEnd: isoDate(year - 1, month + 1, 0),
    }
  }

  const months = MONTHS_BY_TIMEFRAME[timeframe]
  return {
    start: isoDate(year, month - months, 1),
    end: isoDate(year, month, 0),
    previousStart: isoDate(year, month - 2 * months, 1),
    previousEnd: isoDate(year, month - months, 0),
  }
}

export const ratingHistoryStart = ({ start, end }: ReviewsGrowthWindow): string => {
  const [year, month, day] = end.split('-').map(Number)
  const minHistoryStart = isoDate(year, month - 1, day - (MIN_HISTORY_WEEKS * 7 - 1))
  return start < minHistoryStart ? start : minHistoryStart
}

export const comparisonWindow = (timeframe: InsightsTimeframe, today: Date): InsightsComparisonWindow => {
  const window = growthWindow(timeframe, today)

  if (timeframe === InsightsTimeframe.LATEST) {
    const year = today.getFullYear()
    const month = today.getMonth()
    return {
      ...window,
      previousStart: isoDate(year, month - 1, 1),
      previousEnd: isoDate(year, month, 0),
      span: 'latest',
      months: 1,
    }
  }

  if (timeframe === InsightsTimeframe.YTD) return { ...window, span: 'ytd', months: today.getMonth() + 1 }

  return { ...window, span: 'lastMonths', months: MONTHS_BY_TIMEFRAME[timeframe] }
}

const monthStart = (date: string, offset: number): string => isoDate(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1 + offset, 1)

export const segmentSnapshots = ({ start, end }: ReviewsGrowthWindow): { segmentsBefore: string; segmentsEnd: string } => ({
  segmentsBefore: monthStart(start, -1),
  segmentsEnd: monthStart(end, 0),
})

export const isoToday = (today: Date): string => isoDate(today.getFullYear(), today.getMonth(), today.getDate())
